import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Modal, Button, Segment, Header, Icon, Popup } from 'semantic-ui-react';
import styled from 'styled-components';

import { useCredits, useCreditsConfig, CreditPack } from 'src/hooks/useCredits';
import { useHostAppProxy } from 'src/contexts/HostAppProxyContext';

const appleProductIdForPack = (pack: CreditPack) =>
  `io.betareader.credits.${pack.credits}`;

const Columns = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  align-items: stretch;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const Column = styled(Segment)`
  flex: 1;
  min-width: 240px;
  /* reset the semantic Segment :first-child/:last-child margin asymmetry so
     both cards' top edges line up */
  margin: 0 !important;
  display: flex !important;
  flex-direction: column;

  /* keep the header icon + text on one baseline, no stray top margin */
  .ui.header {
    margin-top: 0;
    display: flex;
    align-items: center;
  }
`;

const PackRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
`;

const Spacer = styled.div`
  flex: 1;
`;

type OutOfCreditsModalProps = {
  open: boolean;
  onClose: () => void;
};

const OutOfCreditsModal: React.FC<OutOfCreditsModalProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { data: config } = useCreditsConfig();
  const { data: credits } = useCredits();
  const queryClient = useQueryClient();
  const { settings, purchaseProduct } = useHostAppProxy();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const userProfile = useSelector((state: any) => state.user.userProfile);

  const packs = config?.packs || [];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const paddle = (window as any).Paddle;
  // Credit the account the SERVER resolves for this user (owner-first), echoed
  // from /billing/credits — NOT a client-derived membership, which can differ
  // for multi-account users and land the credits on the wrong account.
  const accountId = credits?.accountId;
  // Ready to open checkout once Paddle is loaded and we know which account to
  // credit. The per-pack Paddle product id is checked separately in `purchasable`.
  const useNativePurchases = Boolean(settings.isWrapped);
  const canCheckout = Boolean(!useNativePurchases && paddle && accountId);

  React.useEffect(() => {
    const status = settings.nativePurchaseEvent?.data?.status;
    if (status === 'success') {
      [1000, 3000, 7000, 15000].forEach(delay =>
        setTimeout(
          () => queryClient.invalidateQueries(['billingCredits']),
          delay
        )
      );
    } else if (status === 'cancelled') {
      toast.info(t('Cancelled', 'Cancelled'));
    } else if (status === 'error') {
      toast.error(
        t('PurchaseFailed', 'The purchase could not be completed.')
      );
    }
  }, [settings.nativePurchaseEvent, queryClient, t]);

  const goToSubscription = () => {
    onClose();
    history.push('/products');
  };

  const buyPack = (pack: CreditPack) => {
    if (useNativePurchases) {
      if (settings.purchaseMode === 'ios_iap') {
        purchaseProduct(appleProductIdForPack(pack));
      }
      return;
    }
    if (!pack.paddleProductId || !canCheckout) return;
    try {
      paddle.Checkout.open({
        // The pack that gets granted is derived server-side from the product
        // Paddle charges for; passthrough only names the account to credit.
        product: pack.paddleProductId,
        email: userProfile?.email,
        passthrough: JSON.stringify({
          accountId,
          creditPack: pack.internalId,
        }),
        title: `${pack.credits} ${t('Credits', 'credits')}`,
        successCallback: () => {
          toast.success(
            t(
              'CreditsPurchasePending',
              'Payment received — your credits will appear shortly.'
            )
          );
          // Credits are granted asynchronously by the payment webhook, which can
          // land anywhere from ~1s to tens of seconds after checkout. Re-fetch
          // the balance a few times so it updates without a manual reload.
          [2000, 5000, 10000, 20000].forEach(delay =>
            setTimeout(
              () => queryClient.invalidateQueries(['billingCredits']),
              delay
            )
          );
        },
      });
    } catch (e) {
      toast.error(t('SomethingWentWrong', 'Something went wrong'));
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="small" closeIcon>
      <Modal.Header>
        {t('KeepTheEditorRunning', 'Keep the editor running')}
      </Modal.Header>
      <Modal.Content>
        <Columns>
          <Column>
            <Header as="h4">
              <Icon name="arrow up" color="orange" />
              {t('UpgradePlan', 'Upgrade plan')}
            </Header>
            <p>
              {t(
                'UpgradePlanDescription',
                'More monthly credits that reset every period.'
              )}
            </p>
            <Spacer />
            <Button color="orange" fluid onClick={goToSubscription}>
              {t('Upgrade', 'Upgrade')}
            </Button>
          </Column>
          <Column>
            <Header as="h4">
              <Icon name="lightning" color="orange" />
              {t('CreditPacks', 'Credit packs')}
            </Header>
            <p>
              {t(
                'CreditPacksDescription',
                'One-time top-ups that do not expire.'
              )}
            </p>
            {packs.map(pack => {
              const appleStoreProduct = settings.nativeProducts?.find(
                product => product.productId === appleProductIdForPack(pack)
              );
              const purchasable = useNativePurchases
                ? settings.purchaseMode === 'ios_iap' &&
                  Boolean(appleStoreProduct)
                : Boolean(pack.paddleProductId && canCheckout);
              return (
                <PackRow key={pack.internalId}>
                  <span>
                    {pack.credits} {t('Credits', 'credits')}
                  </span>
                  {purchasable ? (
                    <Button
                      size="tiny"
                      color="orange"
                      onClick={() => buyPack(pack)}
                    >
                      {appleStoreProduct?.priceString || `$${pack.usd}`}
                    </Button>
                  ) : (
                    <Popup
                      content={t('ComingSoon', 'Coming soon')}
                      position="top center"
                      trigger={
                        <span>
                          <Button size="tiny" basic disabled>
                            ${pack.usd}
                          </Button>
                        </span>
                      }
                    />
                  )}
                </PackRow>
              );
            })}
          </Column>
        </Columns>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>{t('Close', 'Close')}</Button>
      </Modal.Actions>
    </Modal>
  );
};

export default OutOfCreditsModal;
